import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, PhoneIcon } from 'lucide-react';
import { LandingHero } from '../../components/LandingHero';
import { TrustLocalBand } from '../../components/TrustLocalBand';
import { FaqList } from '../../components/FaqList';
import { serviceOptionValue } from '../../components/HeroLeadForm';
import { findCategory } from '../../data/serviceCategories';
import { business } from '../../data/site';
import { usePageMeta } from '../../hooks/usePageMeta';
const reminders = ['An oil life indicator or maintenance reminder is showing', 'You are unsure when the last oil service was performed', 'The vehicle has been driven mostly short trips or in heavy heat', 'You are planning a longer drive and want the vehicle serviced first'];
const bringWithYou = ['Year, make and model', 'Current mileage', 'When the last oil service was performed, if you know', 'Anything else the vehicle has been doing that you want looked at'];
const faqs = [{
  question: 'What is included in the $49.97 price?',
  answer: 'The offer is a 5 qt oil change for $49.97, before taxes and shop fees. Not all oils apply. Diesel and specialty oils do not apply. Contact us for details.'
}, {
  question: 'How often should I change my oil?',
  answer: 'Intervals vary by vehicle and how it is driven, so there is no single answer. Call with your year, make, model and mileage and we can review the factory schedule with you.'
}, {
  question: 'Can you tell me what else my vehicle is due for?',
  answer: 'Yes. While the vehicle is here we can go over what is due now and what can reasonably wait.'
}];
export function OilChangeSpecial() {
  const category = findCategory('preventive-maintenance');
  const related = (category?.services ?? []).slice(0, 8);
  usePageMeta('Oil Change Special in Oak Ridge, TN | East End Automotive Repair', '5 qt oil change for $49.97 before taxes and shop fees at East End Automotive Repair in Oak Ridge, TN. Not all oils apply; diesel and specialty oils excluded. Call (865) 206-2279.');
  return <>
      <LandingHero eyebrow="Oak Ridge, Tennessee" title="Oil Change Special in Oak Ridge, TN" intro="Regular oil service helps protect the engine and gives the shop a chance to review maintenance needs based on your vehicle and its service interval. Call the shop to have the current oil change offer applied." crumbs={[{
      label: 'Specials',
      to: '/specials'
    }, {
      label: 'Oil Change'
    }]} offer={{
      label: '5 qt oil change for $49.97',
      detail: 'Before taxes and shop fees. Not all oils apply. Diesel and specialty oils do not apply. Contact us for details.'
    }} defaultService={serviceOptionValue('preventive-maintenance', 'oil-and-filter-changes')} sourcePage="special-oil-change" data-id="element-896" data-mp="33fw4lamf0e63" />

      <section className="w-full bg-white py-14 lg:py-20" data-id="element-897" data-mp="gj4l2bgtl1hu">
        <div className="mx-auto max-w-shell px-5 lg:px-8" data-id="element-898" data-mp="gil34ur66d3t">
          <div className="grid gap-12 md:grid-cols-2 lg:gap-16" data-id="element-899" data-mp="lh64wym2l4ud">
            <div data-id="element-900" data-mp="q8o53kg9r1ea">
              <h2 className="font-display text-[24px] font-extrabold leading-[1.1] tracking-[-0.02em] text-ink sm:text-[30px]" data-id="element-901" data-mp="q84m00evu6yo">
                Why routine oil service matters
              </h2>
              <p className="mt-4 max-w-[560px] text-[16px] leading-relaxed text-steel/80" data-id="element-902" data-mp="3tpmtu7unndz3">
                Regular oil service helps protect the engine and gives the shop
                a chance to review maintenance needs based on your vehicle and
                its service interval. Call the shop to have the current oil
                change offer applied.
              </p>
              <h3 className="mt-9 font-display text-[18px] font-bold tracking-tight text-ink" data-id="element-903" data-mp="cewxihm3lrl7">
                Reminders it may be time
              </h3>
              <ul className="mt-4 space-y-2.5" data-id="element-904" data-mp="1nv3leiwylt09">
                {reminders.map(item => <li key={item} className="flex items-start gap-2.5 text-[15.5px] leading-snug text-steel/80" data-id="element-905" data-mp="1lijau7pf2vxi">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" aria-hidden="true" data-id="element-906" data-mp="1vggyaszgcop2" />
                    {item}
                  </li>)}
              </ul>
              <p className="mt-5 max-w-[560px] text-[14.5px] leading-relaxed text-steel/65" data-id="element-907" data-mp="24ej5xrdoyflb">
                Service intervals vary by vehicle and driving conditions, so we
                go by your vehicle’s factory schedule rather than one universal
                number.
              </p>
            </div>

            <div data-id="element-908" data-mp="299orgp36ls8v">
              <h2 className="font-display text-[24px] font-extrabold leading-[1.1] tracking-[-0.02em] text-ink sm:text-[30px]" data-id="element-909" data-mp="2o4d2sjv9jkwe">
                Have this ready when you call
              </h2>
              <ul className="mt-5 space-y-2.5" data-id="element-910" data-mp="32rbqc3r01v5u">
                {bringWithYou.map(item => <li key={item} className="flex items-start gap-2.5 text-[15.5px] leading-snug text-steel/80" data-id="element-911" data-mp="36swzbe0expar">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" aria-hidden="true" data-id="element-912" data-mp="2ssvxnwcbqdu8" />
                    {item}
                  </li>)}
              </ul>

              <h3 className="mt-10 font-display text-[18px] font-bold tracking-tight text-ink" data-id="element-913" data-mp="2nuaxty0zhw6b">
                Related maintenance services
              </h3>
              <ul className="mt-4 grid gap-2.5 sm:grid-cols-2" data-id="element-914" data-mp="2a0tjcrn5n9n5">
                {related.map(service => <li key={service.slug} data-id="element-915" data-mp="204qfnhcnn2y3">
                    <Link to={`/services/preventive-maintenance/${service.slug}`} className="group flex items-center justify-between gap-2 rounded-lg border border-black/10 bg-chalk px-3.5 py-3 text-[14px] leading-snug text-steel/85 transition-colors duration-150 ease-out hover:border-ink/40 hover:text-ink" data-id="element-916" data-mp="205rpxjlujler">
                      {service.name}
                      <ArrowRightIcon className="h-4 w-4 shrink-0 text-steel/40 group-hover:text-brand-dark" aria-hidden="true" data-id="element-917" data-mp="1c9n4z6wjcg3h" />
                    </Link>
                  </li>)}
              </ul>
              <Link to="/services/preventive-maintenance" className="mt-5 inline-block text-[12.5px] font-bold uppercase tracking-[0.08em] text-brand-dark hover:underline" data-id="element-918" data-mp="2000x0xf4eey2">
                All preventive maintenance
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-charcoal py-12 text-white lg:py-14" data-id="element-919" data-mp="ji3gb7h669u3">
        <div className="mx-auto flex max-w-shell flex-col gap-6 px-5 sm:flex-row sm:items-center sm:justify-between lg:px-8" data-id="element-920" data-mp="ek1w2j80c4h1">
          <div data-id="element-921" data-mp="2v7t308xbd1zd">
            <h2 className="font-display text-[22px] font-bold tracking-tight text-brand-light sm:text-[26px]" data-id="element-922" data-mp="2v79vlm5ahvzl">
              5 qt oil change for $49.97
            </h2>
            <p className="mt-2 text-[15px] leading-relaxed text-chalk/70" data-id="element-923" data-mp="305308aqy2hxr">
              Before taxes and shop fees. Not all oils apply. Diesel and
              specialty oils do not apply. Contact us for details.
            </p>
          </div>
          <a href={business.phoneHref} className="inline-flex w-full max-w-full shrink-0 items-center justify-center gap-3 self-start whitespace-normal rounded-md bg-brand px-4 py-3.5 text-center font-display text-[17px] font-bold text-white transition-colors duration-150 ease-out hover:bg-brand-light sm:w-auto sm:self-auto sm:whitespace-nowrap sm:px-6" data-id="element-924" data-mp="3dxj7jnqvp24h">
            <PhoneIcon className="h-5 w-5 shrink-0" aria-hidden="true" data-id="element-925" data-mp="1thnywn7rvwtf" />
            Call {business.phone}
          </a>
        </div>
      </section>

      <TrustLocalBand data-id="element-926" data-mp="3vdwtqq7cbjxo" />

      <FaqList faqs={faqs} heading="Oil change questions" data-id="element-927" data-mp="3qgwnae6kwf61" />

      <section className="w-full bg-ink py-14 text-white lg:py-16" data-id="element-928" data-mp="3qgdebmywp145">
        <div className="mx-auto flex max-w-shell flex-col gap-7 px-5 lg:flex-row lg:items-center lg:justify-between lg:px-8" data-id="element-929" data-mp="90on14exlwnl">
          <h2 className="max-w-[600px] font-display text-[26px] font-extrabold leading-[1.1] tracking-[-0.02em] sm:text-[32px]" data-id="element-930" data-mp="41u2ny1bg01d">
            Call East End for an oil change in Oak Ridge, TN
          </h2>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center lg:shrink-0" data-id="element-931" data-mp="37jvh4q1ag90i">
            <a href={business.phoneHref} className="inline-flex w-full max-w-full items-center justify-center gap-3 whitespace-normal rounded-md bg-brand px-4 py-4 text-center font-display text-lg font-bold text-white transition-colors duration-150 ease-out hover:bg-brand-light sm:w-auto sm:whitespace-nowrap sm:px-7" data-id="element-932" data-mp="3mgwasz31ie1m">
              <PhoneIcon className="h-5 w-5 shrink-0" aria-hidden="true" data-id="element-933" data-mp="148z60o80bcji" />
              Call {business.phone}
            </a>
            <Link to="/specials" className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-white/25 px-7 py-4 text-base font-semibold text-white transition-colors duration-150 ease-out hover:border-white/60" data-id="element-934" data-mp="199ejdfsagk1h">
              All Specials
            </Link>
          </div>
        </div>
      </section>
    </>;
}